import { Platform, type PlatformType } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bButlerService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-butler-service'
import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { cartFormB2bSchema, type CartFormValues } from '@cart-services/zod-schema/cartFormSchema'
import { CartFormProvider } from '@cart-ui/CartFormProvider/CartFormProvider'
import { type CountryCode, type ProjectCode } from '@constants'
import { zodResolver } from '@hookform/resolvers/zod'
import { type DeliveryClass } from '@hooks-api/types/dataModel/shipping'
import { createContext, useContext, useMemo, useState, type Dispatch, type PropsWithChildren, type SetStateAction } from 'react'
import { useForm } from 'react-hook-form'

export interface CartProviderProps {
  countryCode: CountryCode
  projectCode: ProjectCode
  campaignId: number
  currency?: string
  campaignCodeFromUrl?: string
  deliveryClass?: DeliveryClass
}

export interface CartContextProps extends CartProviderProps {
  platform: PlatformType
  isAiCart: boolean
  fallbackUrl?: string
  discardAllRedirectUrl?: string
  checkoutUrl?: string
  isCartInfoValidating: boolean
  setIsCartInfoValidating: Dispatch<SetStateAction<boolean>>
}

export const CartContext = createContext<CartContextProps | null>(null)

export const CartProvider = (props: PropsWithChildren<CartProviderProps>) => {
  const { children, countryCode, projectCode, campaignId, currency, campaignCodeFromUrl } = props
  const { orderId } = useFpB2bButlerService(campaignId, Platform.MERCHANT)
  const { items: cartItems } = useFpB2bCartService(campaignId, Platform.MERCHANT)
  const [isCartInfoValidating, setIsCartInfoValidating] = useState(false)
  const form = useForm<CartFormValues>({
    resolver: zodResolver(cartFormB2bSchema),
  })

  const fallbackUrl = `/merchant/${campaignCodeFromUrl}/product-catalog`
  const discardAllRedirectUrl = `/merchant/${campaignCodeFromUrl}/product-catalog`
  const checkoutUrl = `/merchant/${campaignCodeFromUrl}/checkout`

  /**
   * 購物車目前常溫和冷凍是分開的，所以只取第一個項目就可，但未來應該要改成和 WP 官網一樣
   */
  const deliveryClass = cartItems[0]?.delivery_class

  const contextValue = useMemo(() => ({
    countryCode,
    projectCode,
    campaignId,
    platform: Platform.MERCHANT as PlatformType,
    isAiCart: !!orderId,
    currency,
    checkoutUrl,
    fallbackUrl,
    campaignCodeFromUrl,
    discardAllRedirectUrl,
    isCartInfoValidating,
    setIsCartInfoValidating,
    deliveryClass,
  }), [campaignCodeFromUrl, campaignId, countryCode, currency, discardAllRedirectUrl, fallbackUrl, isCartInfoValidating, orderId, projectCode, checkoutUrl, setIsCartInfoValidating, deliveryClass])

  return (
    <CartContext.Provider value={contextValue}>
      <CartFormProvider {...form}>
        {children}
      </CartFormProvider>
    </CartContext.Provider>
  )
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) throw new Error('useCart must be used within a CartProvider')
  return context
}
